import type { RouteRepository } from '../../application/RouteRepository'
import type { RouteDataSource } from '../../domain/route'
import { captureRadiusBounds, fallbackCaptureRadiusMeters } from '../../domain/captureRadius'
import type { FlagVerification, FlagVerificationMethod, FlagWalkState } from '../../domain/flagWalk'
import { roundCoordinate } from '../../domain/positionAdjustment'
import { metersBetween } from '../../domain/streetSnap'
import {
  isAcceptableRouteColor,
  RouteMutationError,
  type PlannedLocation,
  type PlanningRoute,
  type PlacementRefusal,
} from '../../domain/routePlanning'

// Données de DÉMONSTRATION (mode fixtures) : adresses fictives, mêmes règles que le serveur
// (versions, ancres, lots atomiques, gris réservé) pour que l'écran se comporte comme en réel.

type PlanningOperations = Pick<
  RouteRepository,
  | 'getPlanningSnapshot'
  | 'ensureSeasonRoutes'
  | 'createRoute'
  | 'updateRoute'
  | 'previewPlacement'
  | 'placeLocations'
  | 'removeLocation'
  | 'setLocationPosition'
  | 'getMapSettings'
  | 'setMapSettings'
  | 'getFlagWalkState'
  | 'verifyFlag'
  | 'undoFlagVerification'
  | 'setFlagWalkProgress'
>

interface FixtureLocation extends Omit<PlannedLocation, 'routeId' | 'position' | 'adjusted'> {
  season: string
  eligible: boolean
  /** Position ajustée, distincte du géocodage (`latitude`/`longitude`), comme en base. */
  adjustedLatitude: number | null
  adjustedLongitude: number | null
}

interface FixtureFlag {
  latitude: number | null
  longitude: number | null
  source: string | null
  version: number
  /** Rayon de capture propre à la résidence ; nul = défaut. */
  captureRadiusM: number | null
}

/** Vérification manuelle d'un drapeau (même modèle que `service_location_flag_verifications`). */
interface FixtureVerification {
  id: string
  locationId: string
  routeId: string
  flagVersion: number
  method: FlagVerificationMethod
  before: FixtureFlag & { verificationId: string | null }
  verifiedAt: string
  restoredFrom: string | null
  revoked: boolean
}

interface FixtureRoute extends Omit<PlanningRoute, 'memberCount'> {
  members: string[]
}

const demoSeason = '2026-2027'

function demoLocation(
  id: string,
  addressLine1: string,
  clientName: string,
  coordinates: [number, number] | null,
  extra: Partial<FixtureLocation> = {},
): FixtureLocation {
  return {
    id,
    contractId: `demo-contract-${id}`,
    clientPropertyId: null,
    clientName,
    addressLine1,
    addressLine2: null,
    city: 'Ville Démo',
    postalCode: null,
    latitude: coordinates ? coordinates[0] : null,
    longitude: coordinates ? coordinates[1] : null,
    positionVersion: 1,
    adjustedLatitude: null,
    adjustedLongitude: null,
    season: demoSeason,
    eligible: true,
    ...extra,
  }
}

export function demoPlanningLocations(): FixtureLocation[] {
  return [
    demoLocation('demo-loc-01', '12 Rue des Érables', 'Démo Tremblay', [45.7801, -74.0032]),
    demoLocation('demo-loc-02', '14 Rue des Érables', 'Démo Gagnon', [45.7803, -74.0029]),
    demoLocation('demo-loc-03', '3 Rue des Érables', 'Démo Roy', [45.7797, -74.0041]),
    demoLocation('demo-loc-04', '101 Rue des Érables', 'Démo Côté', [45.7812, -74.0011], {
      addressLine2: 'Unité 4',
    }),
    demoLocation('demo-loc-05', '8 Montée Démo', 'Démo Bouchard', [45.7765, -74.0102]),
    demoLocation(
      'demo-loc-06',
      '33 Boulevard du Très Long Nom de Rue de Démonstration',
      'Entreprise de démonstration au nom particulièrement long inc.',
      [45.7752, -74.0124],
    ),
    demoLocation('demo-loc-07', '5 Chemin Sans-Coordonnées', 'Démo Gauthier', null),
    demoLocation('demo-loc-08', '17 Rue du Lac Démo', 'Démo Morin', [45.7839, -73.9987]),
    demoLocation('demo-loc-09', '19 Rue du Lac Démo', 'Démo Lavoie', [45.7841, -73.9984]),
    demoLocation('demo-loc-10', '2 Place Démo', 'Démo Fortin', null, { addressLine2: 'App. 2' }),
  ]
}

function plannedFields(location: FixtureLocation): Omit<PlannedLocation, 'routeId' | 'position'> {
  const adjusted = location.adjustedLatitude !== null && location.adjustedLongitude !== null
  return {
    id: location.id,
    contractId: location.contractId,
    clientPropertyId: location.clientPropertyId,
    clientName: location.clientName,
    addressLine1: location.addressLine1,
    addressLine2: location.addressLine2,
    city: location.city,
    postalCode: location.postalCode,
    latitude: adjusted ? location.adjustedLatitude : location.latitude,
    longitude: adjusted ? location.adjustedLongitude : location.longitude,
    adjusted,
    positionVersion: location.positionVersion,
  }
}

const civicPattern = /^\s*(\d+)/
const streetPattern = /^\s*\d+[a-zA-Z]?\s*[-,]?\s*/

function sourceOrder(left: FixtureLocation, right: FixtureLocation): number {
  const street = (value: string | null) => (value ?? '').replace(streetPattern, '').trim().toLowerCase()
  const civic = (value: string | null) => {
    const match = civicPattern.exec(value ?? '')
    return match ? Number(match[1]) : Number.POSITIVE_INFINITY
  }
  return (
    street(left.addressLine1).localeCompare(street(right.addressLine1)) ||
    civic(left.addressLine1) - civic(right.addressLine1) ||
    (left.addressLine1 ?? '').localeCompare(right.addressLine1 ?? '') ||
    (left.addressLine2 ?? '').localeCompare(right.addressLine2 ?? '') ||
    left.id.localeCompare(right.id)
  )
}

export function createFixtureRoutePlanning(
  source: RouteDataSource,
  locations: FixtureLocation[] = demoPlanningLocations(),
): PlanningOperations {
  const routes: FixtureRoute[] = []
  const commands = new Map<string, unknown>()
  let sequence = 0
  let detectionRadiusMeters = 0

  /** Nouvelle version de la route, datée comme `routes.updated_at` en base. */
  const touch = (route: FixtureRoute) => {
    route.version += 1
    route.updatedAt = new Date().toISOString()
  }
  const routeOf = (locationId: string) => routes.find((route) => route.members.includes(locationId)) ?? null
  const activeRoute = (routeId: string) => {
    const route = routes.find((candidate) => candidate.id === routeId)
    if (!route) throw new RouteMutationError('invalid', 'ROUTE_NOT_FOUND')
    return route
  }
  // Ajustement maison par maison (démonstration) : drapeaux, vérifications et point de reprise en mémoire.
  const flags = new Map<string, FixtureFlag>()
  const verifications: FixtureVerification[] = []
  const progress = new Map<
    string,
    { currentLocationId: string | null; todoOnly: boolean; updatedAt: string }
  >()
  const flagOf = (location: FixtureLocation): FixtureFlag =>
    flags.get(location.id) ?? {
      latitude: location.flagLatitude ?? null,
      longitude: location.flagLongitude ?? null,
      source: location.flagSource ?? null,
      version: 0,
      captureRadiusM: null,
    }
  const validVerification = (locationId: string, version: number): FlagVerification | null => {
    const found = [...verifications]
      .reverse()
      .find((item) => item.locationId === locationId && item.flagVersion === version && !item.revoked)
    return found
      ? {
          id: found.id,
          method: found.method,
          verifiedAt: found.verifiedAt,
          byName: 'Démonstration',
          restored: found.restoredFrom !== null,
        }
      : null
  }
  const memberOf = (routeId: string, locationId: string) =>
    routes.some((route) => route.id === routeId && route.members.includes(locationId))
  const saveProgress = (routeId: string, currentLocationId: string | null, todoOnly?: boolean) => {
    progress.set(routeId, {
      currentLocationId,
      todoOnly: todoOnly ?? progress.get(routeId)?.todoOnly ?? false,
      updatedAt: new Date().toISOString(),
    })
  }
  const remember = <T>(key: string, result: T): T => {
    commands.set(key, result)
    return result
  }
  const refusals = (season: string, ids: readonly string[]): PlacementRefusal[] =>
    ids.flatMap((id): PlacementRefusal[] => {
      const location = locations.find((candidate) => candidate.id === id)
      if (!location) return [{ locationId: id, reason: 'NOT_FOUND' }]
      if (location.season !== season) return [{ locationId: id, reason: 'OTHER_SEASON' }]
      return location.eligible ? [] : [{ locationId: id, reason: 'INELIGIBLE' }]
    })

  return {
    getPlanningSnapshot(season) {
      const eligible = locations.filter((location) => location.eligible && location.season === season)
      const planned: PlannedLocation[] = [...eligible].sort(sourceOrder).map((location) => {
        const route = routeOf(location.id)
        const flag = flagOf(location)
        return {
          ...plannedFields(location),
          flagLatitude: flag.latitude,
          flagLongitude: flag.longitude,
          flagSource: flag.source,
          flagVersion: flag.version,
          flagVerification: validVerification(location.id, flag.version),
          flagCaptureRadiusM: flag.captureRadiusM,
          routeId: route?.id ?? null,
          position: route ? route.members.indexOf(location.id) + 1 : null,
        }
      })
      return Promise.resolve({
        season,
        seasons: [demoSeason],
        routes: routes
          .filter((route) => route.season === season)
          .map(({ members, ...route }) => ({ ...route, memberCount: members.length })),
        locations: planned,
        totals: {
          eligible: planned.length,
          unassigned: planned.filter((location) => location.routeId === null).length,
          unlocated: planned.filter((location) => location.latitude === null).length,
          unassignedUnlocated: planned.filter(
            (location) => location.routeId === null && location.latitude === null,
          ).length,
        },
        recentIneligibleRemovals: [],
        meta: { source, retrievedAt: new Date().toISOString() },
      })
    },
    ensureSeasonRoutes(season) {
      if (routes.some((route) => route.season === season)) return Promise.resolve({ created: 0 })
      ;['#3B82F6', '#F5B82E', '#22C55E', '#A855F7'].forEach((color, index) => {
        routes.push({
          id: `demo-route-${String(++sequence)}`,
          name: `Route ${String(index + 1)}`,
          color,
          description: null,
          season,
          displayOrder: index + 1,
          version: 1,
          updatedAt: new Date().toISOString(),
          updatedByName: null,
          members: [],
        })
      })
      return Promise.resolve({ created: 4 })
    },
    createRoute(command) {
      const previous = commands.get(command.idempotencyKey) as { routeId: string } | undefined
      if (previous) return Promise.resolve(previous)
      if (!isAcceptableRouteColor(command.color)) {
        return Promise.reject(new RouteMutationError('invalid', 'ROUTE_COLOR_RESERVED'))
      }
      const seasonal = routes.filter((route) => route.season === command.season)
      if (seasonal.some((route) => route.name.toLowerCase() === command.name.trim().toLowerCase())) {
        return Promise.reject(new RouteMutationError('invalid', 'ROUTE_NAME_TAKEN'))
      }
      const route: FixtureRoute = {
        id: `demo-route-${String(++sequence)}`,
        name: command.name.trim(),
        color: command.color.toUpperCase(),
        description: null,
        season: command.season,
        displayOrder: seasonal.length + 1,
        version: 1,
        updatedAt: new Date().toISOString(),
        updatedByName: null,
        members: [],
      }
      routes.push(route)
      return Promise.resolve(remember(command.idempotencyKey, { routeId: route.id }))
    },
    updateRoute(command) {
      const previous = commands.get(command.idempotencyKey) as { routeId: string } | undefined
      if (previous) return Promise.resolve(previous)
      try {
        const route = activeRoute(command.routeId)
        if (command.color !== undefined && !isAcceptableRouteColor(command.color)) {
          throw new RouteMutationError('invalid', 'ROUTE_COLOR_RESERVED')
        }
        if (command.description !== undefined) {
          const description = command.description?.trim() ?? ''
          if (description.length > 500) throw new RouteMutationError('invalid', 'ROUTE_DESCRIPTION_INVALID')
          route.description = description === '' ? null : description
        }
        if (command.name !== undefined) route.name = command.name.trim()
        if (command.color !== undefined) route.color = command.color.toUpperCase()
        route.updatedAt = new Date().toISOString()
        return Promise.resolve(remember(command.idempotencyKey, { routeId: route.id }))
      } catch (error) {
        return Promise.reject(error instanceof Error ? error : new Error(String(error)))
      }
    },
    previewPlacement(input) {
      const target = routes.find((route) => route.id === input.routeId)
      if (!target?.season) return Promise.reject(new Error('ROUTE_NOT_FOUND'))
      const refused = refusals(target.season, input.locationIds)
      const refusedIds = new Set(refused.map((refusal) => refusal.locationId))
      const accepted = [...new Set(input.locationIds)].filter((id) => !refusedIds.has(id))
      const moved = new Map<string, number>()
      let unassigned = 0
      let alreadyInRoute = 0
      for (const id of accepted) {
        const current = routeOf(id)
        if (!current) unassigned += 1
        else if (current.id === target.id) alreadyInRoute += 1
        else moved.set(current.id, (moved.get(current.id) ?? 0) + 1)
      }
      return Promise.resolve({
        routeId: target.id,
        unassigned,
        alreadyInRoute,
        movedFrom: [...moved].map(([routeId, count]) => ({ routeId, count })),
        refused,
      })
    },
    placeLocations(command) {
      const previous = commands.get(command.idempotencyKey)
      if (previous)
        return Promise.resolve(previous as Awaited<ReturnType<PlanningOperations['placeLocations']>>)
      try {
        const target = activeRoute(command.routeId)
        if (!target.season) throw new RouteMutationError('invalid', 'ROUTE_SEASON_MISSING')
        const ids = [...new Set(command.locationIds)]
        const anchor = command.beforeLocationId ?? command.afterLocationId ?? null
        if (anchor && (ids.length !== 1 || anchor === ids[0]))
          throw new RouteMutationError('invalid', 'ROUTE_ANCHOR_INVALID')
        const refused = refusals(target.season, ids)
        if (refused.length > 0) return Promise.resolve({ status: 'REJECTED' as const, refused })

        const touched = [target, ...ids.map(routeOf).filter((route): route is FixtureRoute => route !== null)]
        for (const route of touched) {
          if (command.expectedVersions[route.id] !== route.version) {
            throw new RouteMutationError('conflict', 'ROUTE_VERSION_CONFLICT')
          }
        }
        if (anchor && !target.members.includes(anchor))
          throw new RouteMutationError('conflict', 'ROUTE_ANCHOR_CHANGED')

        const alreadyInRoute = ids.filter((id) => target.members.includes(id)).length
        let moved = 0
        for (const route of touched) {
          if (route === target) continue
          const before = route.members.length
          route.members = route.members.filter((id) => !ids.includes(id))
          if (route.members.length !== before) {
            moved += before - route.members.length
            touch(route)
          }
        }
        const fresh = locations.filter(
          (location) => ids.includes(location.id) && !target.members.includes(location.id),
        )
        let reordered = false
        if (anchor) {
          const moving = ids[0]
          if (moving === undefined) throw new RouteMutationError('invalid', 'ROUTE_ANCHOR_INVALID')
          reordered = target.members.includes(moving)
          const others = target.members.filter((id) => id !== moving)
          const index = others.indexOf(anchor) + (command.afterLocationId ? 1 : 0)
          target.members = [...others.slice(0, index), moving, ...others.slice(index)]
        } else {
          target.members = [...target.members, ...[...fresh].sort(sourceOrder).map((location) => location.id)]
        }
        touch(target)
        const result = {
          status: 'APPLIED' as const,
          routeId: target.id,
          placed: fresh.length,
          movedFromOtherRoutes: moved,
          alreadyInRoute,
          reordered,
          routeVersions: Object.fromEntries(
            [...new Set(touched)].map((route) => [route.id, route.version] as const),
          ),
        }
        return Promise.resolve(remember(command.idempotencyKey, result))
      } catch (error) {
        return Promise.reject(error instanceof Error ? error : new Error(String(error)))
      }
    },
    removeLocation(command) {
      const previous = commands.get(command.idempotencyKey)
      if (previous)
        return Promise.resolve(previous as Awaited<ReturnType<PlanningOperations['removeLocation']>>)
      try {
        const route = activeRoute(command.routeId)
        if (route.version !== command.expectedVersion)
          throw new RouteMutationError('conflict', 'ROUTE_VERSION_CONFLICT')
        const index = route.members.indexOf(command.locationId)
        if (index < 0) throw new RouteMutationError('conflict', 'ROUTE_LOCATION_NOT_IN_ROUTE')
        const afterLocationId = index > 0 ? (route.members[index - 1] ?? null) : null
        const beforeLocationId = route.members[index + 1] ?? null
        route.members = route.members.filter((id) => id !== command.locationId)
        touch(route)
        return Promise.resolve(
          remember(command.idempotencyKey, {
            routeId: route.id,
            routeVersion: route.version,
            undo: { routeId: route.id, locationId: command.locationId, afterLocationId, beforeLocationId },
          }),
        )
      } catch (error) {
        return Promise.reject(error instanceof Error ? error : new Error(String(error)))
      }
    },
    setLocationPosition(command) {
      const previous = commands.get(command.idempotencyKey)
      if (previous)
        return Promise.resolve(previous as Awaited<ReturnType<PlanningOperations['setLocationPosition']>>)
      const location = locations.find((candidate) => candidate.id === command.locationId)
      if (!location) return Promise.reject(new RouteMutationError('invalid', 'POSITION_LOCATION_NOT_FOUND'))
      if (location.positionVersion !== command.expectedVersion)
        return Promise.reject(new RouteMutationError('conflict', 'POSITION_VERSION_CONFLICT'))
      const reset = command.latitude === null || command.longitude === null
      if (reset && location.latitude === null)
        return Promise.reject(new RouteMutationError('invalid', 'POSITION_NO_GEOCODE'))
      const before = {
        adjustedLatitude: location.adjustedLatitude,
        adjustedLongitude: location.adjustedLongitude,
      }
      location.adjustedLatitude =
        reset || command.latitude === null ? null : roundCoordinate(command.latitude)
      location.adjustedLongitude =
        reset || command.longitude === null ? null : roundCoordinate(command.longitude)
      location.positionVersion += 1
      const effective = plannedFields(location)
      return Promise.resolve(
        remember(command.idempotencyKey, {
          locationId: location.id,
          latitude: effective.latitude ?? 0,
          longitude: effective.longitude ?? 0,
          adjusted: effective.adjusted,
          version: location.positionVersion,
          previous: before,
        }),
      )
    },
    getMapSettings() {
      return Promise.resolve({ detectionRadiusMeters })
    },
    getFlagWalkState(routeId) {
      const route = routes.find((candidate) => candidate.id === routeId)
      if (!route) return Promise.reject(new Error('ROUTE_NOT_FOUND'))
      const members = route.members.flatMap((id, index) => {
        const location = locations.find((candidate) => candidate.id === id)
        if (!location) return []
        const effective = plannedFields(location)
        const flag = flagOf(location)
        return [
          {
            id,
            position: index + 1,
            clientName: location.clientName,
            addressLine1: location.addressLine1,
            addressLine2: location.addressLine2,
            city: location.city,
            latitude: effective.latitude,
            longitude: effective.longitude,
            flagLatitude: flag.latitude,
            flagLongitude: flag.longitude,
            flagSource: flag.source,
            flagVersion: flag.version,
            verification: validVerification(id, flag.version),
            addressLatitude: location.latitude,
            addressLongitude: location.longitude,
            captureRadiusM: flag.captureRadiusM,
          },
        ]
      })
      const last = [...verifications]
        .reverse()
        .find((item) => item.routeId === routeId && item.restoredFrom === null && !item.revoked)
      const state: FlagWalkState = {
        route: {
          id: route.id,
          name: route.name,
          color: route.color,
          season: route.season,
          version: route.version,
        },
        members,
        progress: progress.get(routeId) ?? null,
        lastAction: last
          ? {
              verificationId: last.id,
              locationId: last.locationId,
              method: last.method,
              flagChanged: last.before.version !== last.flagVersion,
              verifiedAt: last.verifiedAt,
              undoable: (flags.get(last.locationId)?.version ?? 0) === last.flagVersion,
            }
          : null,
        defaultCaptureRadiusM: fallbackCaptureRadiusMeters,
        profileWorkRadiusM: null,
      }
      return Promise.resolve(structuredClone(state))
    },
    verifyFlag(command) {
      const previous = commands.get(command.idempotencyKey)
      if (previous) return Promise.resolve(previous as Awaited<ReturnType<PlanningOperations['verifyFlag']>>)
      const location = locations.find((candidate) => candidate.id === command.locationId)
      if (!location || !memberOf(command.routeId, command.locationId))
        return Promise.reject(new RouteMutationError('invalid', 'FLAG_WALK_NOT_MEMBER'))
      if (command.nextLocationId !== null && !memberOf(command.routeId, command.nextLocationId))
        return Promise.reject(new RouteMutationError('invalid', 'FLAG_WALK_NOT_MEMBER'))
      const flag = flagOf(location)
      if (flag.version !== command.expectedFlagVersion)
        return Promise.reject(new RouteMutationError('conflict', 'FLAG_VERSION_CONFLICT'))
      const effective = plannedFields(location)
      const reference =
        effective.latitude !== null && effective.longitude !== null
          ? { lat: effective.latitude, lng: effective.longitude }
          : null
      const radius = command.radius.mode === 'CUSTOM' ? command.radius.meters : null
      if (
        radius !== null &&
        (!Number.isFinite(radius) ||
          radius < captureRadiusBounds.min ||
          radius > captureRadiusBounds.max ||
          (radius * 2) % 1 !== 0)
      )
        return Promise.reject(new RouteMutationError('invalid', 'FLAG_RADIUS_INVALID'))
      let next: { lat: number; lng: number }
      let moved = false
      if (command.latitude !== undefined && command.longitude !== undefined) {
        if (!reference) return Promise.reject(new RouteMutationError('invalid', 'FLAG_WALK_UNLOCATED'))
        next = { lat: roundCoordinate(command.latitude), lng: roundCoordinate(command.longitude) }
        if (metersBetween(reference, next) > 100)
          return Promise.reject(new RouteMutationError('invalid', 'FLAG_TOO_FAR'))
        moved =
          flag.latitude === null
            ? next.lat !== roundCoordinate(reference.lat) || next.lng !== roundCoordinate(reference.lng)
            : next.lat !== flag.latitude || next.lng !== flag.longitude
      } else if (flag.latitude === null || flag.longitude === null) {
        if (!reference) return Promise.reject(new RouteMutationError('invalid', 'FLAG_WALK_UNLOCATED'))
        next = { lat: roundCoordinate(reference.lat), lng: roundCoordinate(reference.lng) }
      } else {
        next = { lat: flag.latitude, lng: flag.longitude }
      }
      const positionChanged = next.lat !== flag.latitude || next.lng !== flag.longitude
      const changed = positionChanged || radius !== flag.captureRadiusM
      const method: FlagVerificationMethod = moved ? 'MOVED' : 'CONFIRMED'
      const after: FixtureFlag = changed
        ? {
            latitude: next.lat,
            longitude: next.lng,
            source: positionChanged ? 'MANUAL' : flag.source,
            version: flag.version + 1,
            captureRadiusM: radius,
          }
        : flag
      flags.set(location.id, after)
      const verification: FixtureVerification = {
        id: crypto.randomUUID(),
        locationId: location.id,
        routeId: command.routeId,
        flagVersion: after.version,
        method,
        before: { ...flag, verificationId: validVerification(location.id, flag.version)?.id ?? null },
        verifiedAt: new Date().toISOString(),
        restoredFrom: null,
        revoked: false,
      }
      verifications.push(verification)
      saveProgress(command.routeId, command.nextLocationId, command.todoOnly)
      return Promise.resolve(
        remember(command.idempotencyKey, {
          status: 'VERIFIED' as const,
          locationId: location.id,
          verification: { id: verification.id, method, flagVersion: after.version, flagChanged: changed },
          flag: {
            latitude: next.lat,
            longitude: next.lng,
            source: after.source,
            version: after.version,
            captureRadiusM: after.captureRadiusM,
          },
        }),
      )
    },
    undoFlagVerification(command) {
      const previous = commands.get(command.idempotencyKey)
      if (previous)
        return Promise.resolve(previous as Awaited<ReturnType<PlanningOperations['undoFlagVerification']>>)
      const verification = verifications.find(
        (item) => item.id === command.verificationId && item.restoredFrom === null,
      )
      if (!verification)
        return Promise.reject(new RouteMutationError('invalid', 'FLAG_VERIFICATION_NOT_FOUND'))
      if (verification.revoked)
        return Promise.reject(new RouteMutationError('invalid', 'FLAG_VERIFICATION_ALREADY_UNDONE'))
      const current = flags.get(verification.locationId)
      if (current?.version !== verification.flagVersion) {
        return Promise.resolve(
          remember(command.idempotencyKey, {
            status: 'CONFLICT' as const,
            reason: 'FLAG_CHANGED_SINCE',
            verificationId: verification.id,
            locationId: verification.locationId,
          }),
        )
      }
      verification.revoked = true
      const changed = verification.before.version !== verification.flagVersion
      if (changed) {
        const restored: FixtureFlag = {
          latitude: verification.before.latitude,
          longitude: verification.before.longitude,
          source: verification.before.source,
          version: current.version + 1,
          captureRadiusM: verification.before.captureRadiusM,
        }
        flags.set(verification.locationId, restored)
        const original = verifications.find((item) => item.id === verification.before.verificationId)
        if (original) {
          verifications.push({
            ...original,
            id: crypto.randomUUID(),
            flagVersion: restored.version,
            restoredFrom: original.id,
            revoked: false,
          })
        }
      }
      saveProgress(verification.routeId, verification.locationId)
      return Promise.resolve(
        remember(command.idempotencyKey, {
          status: 'UNDONE' as const,
          verificationId: verification.id,
          locationId: verification.locationId,
          flagRestored: changed,
        }),
      )
    },
    setFlagWalkProgress(command) {
      if (command.locationId !== null && !memberOf(command.routeId, command.locationId))
        return Promise.reject(new RouteMutationError('invalid', 'FLAG_WALK_NOT_MEMBER'))
      saveProgress(command.routeId, command.locationId, command.todoOnly)
      return Promise.resolve()
    },
    setMapSettings(settings) {
      if (
        !Number.isFinite(settings.detectionRadiusMeters) ||
        settings.detectionRadiusMeters < 0 ||
        settings.detectionRadiusMeters > 5000
      )
        return Promise.reject(new RouteMutationError('invalid', 'ROUTE_RADIUS_INVALID'))
      detectionRadiusMeters = Math.round(settings.detectionRadiusMeters * 100) / 100
      return Promise.resolve({ detectionRadiusMeters })
    },
  }
}
